import styled from '@emotion/styled';
import type { ObjectId } from '@racemap/sdk/schema/base';
import { RacemapColors } from '@racemap/utilities/consts/common';
import type { PathInto, TypeOfPath } from '@racemap/utilities/types/utils';
import classNames from 'classnames';
import { produce } from 'immer';
import React, { type CSSProperties, type ReactElement, useState } from 'react';
import { IconSortDown, IconSortUp } from 'src/components/Icon';
import { makeGetter } from '../../../lib/FormUtils';
import { isValidReactNode } from '../../../lib/validation';
import { AnimatedArrow } from '../AnimatedArrow';

export interface Entry extends Record<string, unknown> {
  id: ObjectId | string;
}

export type Column<T extends Entry, K extends PathInto<T> = PathInto<T>> = {
  header: React.ReactNode;
  headerClassName?: string;
  headerTitle?: string;
  width?: string;
  minWidth?: string;
  dataKey: K;
  cellClassName?: string;
  cellStyle?: CSSProperties;
  key: string;
  sortable?: boolean;
  cellRenderer?: (cellValue: TypeOfPath<T, K> | null, rowData: T, index: number) => React.ReactNode;
};
interface Props<T extends Entry> {
  columns: Array<Column<T, PathInto<T>>>;
  data: Array<T> | IterableIterator<T>;
  getRowStyle?: (rowData: T) => CSSProperties;
  getRowIsExpandable?: (rowData: T) => boolean;
  getRowId?: (rowData: T, index: number) => string | ObjectId;
  subRowRenderer?: (rowData: T, index: number, isExpanded: boolean) => React.ReactNode;
  className?: string;
  style?: CSSProperties;
  defaultExpanded?: boolean;
  expandable?: boolean;
  withSorting?: boolean;
  defaultSortKey?: string;
}

type SortDirection = 'asc' | 'desc';

export function SimpleTable<T extends Entry>({
  columns,
  data,
  getRowStyle,
  subRowRenderer,
  getRowId,
  getRowIsExpandable,
  style,
  defaultExpanded = false,
  expandable = false,
  withSorting = false,
  defaultSortKey,
}: Props<T>): ReactElement {
  const [rowExpandedList, setRowExpandedList] = useState<Map<string, boolean>>(new Map());
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [sortBy, setSortBy] = useState<string | null>(defaultSortKey || null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const withSubRows = subRowRenderer != null;
  const columnWidths = columns.reduce(
    (pV, cV) =>
      `${pV} ${
        cV.minWidth != null ? `minmax(${cV.minWidth}, ${cV.width || '1fr'})` : cV.width || '1fr'
      }`,
    withSubRows ? '35px ' : '',
  );
  const numberOfRows = Array.from(data).length;
  const collapsionNeeded = expandable && numberOfRows > 2;

  const handleToggle = (rowId: string | ObjectId) => {
    setRowExpandedList((curV) =>
      produce(curV, (d) => {
        d.set(rowId.toString(), !d.get(rowId.toString()));
      }),
    );
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLTableCellElement>,
    rowId: string | ObjectId,
  ) => {
    if (e.key === 'Enter') {
      handleToggle(rowId);
    }
  };

  const handleCollapse = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!collapsionNeeded) return;
    setExpanded(!expanded);
  };

  const makeSort = (a: T, b: T) => {
    const aValue = makeGetter(a)(sortBy);
    const bValue = makeGetter(b)(sortBy);

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return -1;
    if (bValue == null) return 1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
  };
  const sortedData = sortBy != null ? Array.from(data).sort(makeSort) : Array.from(data);

  return (
    <Container>
      <Table
        columnWidths={columnWidths}
        style={style}
        expandable={collapsionNeeded}
        expanded={!expandable || expanded}
      >
        <thead>
          <tr>
            {withSubRows && <th />}
            {columns.map((c) => {
              const iconStyle = {
                color: c.header !== sortBy ? RacemapColors.Gray : RacemapColors.PaleBlue,
                fontSize: '0.8em',
                paddingLeft: 5,
              };

              const sortByKey = (key: string, direction: SortDirection) => {
                setSortBy(key);
                setSortDirection(direction);
              };

              return (
                <th key={`header_${c.key}`} className={c.headerClassName} title={c.headerTitle}>
                  {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                  <span
                    style={{ cursor: c.sortable ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (withSorting && c.sortable) {
                        sortByKey(c.key, sortDirection === 'asc' ? 'desc' : 'asc');
                      }
                    }}
                  >
                    {c.header}{' '}
                  </span>
                  {withSorting &&
                    c.sortable &&
                    sortBy === c.key &&
                    (sortDirection === 'desc' ? (
                      <IconSortUp style={iconStyle} onClick={() => sortByKey(c.key, 'asc')} />
                    ) : (
                      <IconSortDown style={iconStyle} onClick={() => sortByKey(c.key, 'desc')} />
                    ))}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {Array.from(sortedData).map((r, i) => {
            const rowId = getRowId?.(r, i)?.toString() || r.id.toString();
            const rowExpanded = !!rowExpandedList.get(rowId);
            return (
              <React.Fragment key={`row_f_${rowId}`}>
                <tr
                  key={`row_${rowId}`}
                  className={classNames('row', { even: i % 2 === 0, odd: i % 2 !== 0 })}
                >
                  {withSubRows &&
                    (getRowIsExpandable == null || !!getRowIsExpandable?.(r) ? (
                      <td
                        onClick={() => handleToggle(rowId)}
                        onKeyUp={(e) => handleKeyPress(e, rowId)}
                        className="center expand-button"
                      >
                        <AnimatedArrow isDown={rowExpanded} />
                      </td>
                    ) : (
                      <td style={{ gridColumn: 1 }} />
                    ))}
                  {columns.map((c) => {
                    const getter = makeGetter(r);
                    const cellValue = getter(c.dataKey);
                    const content = c.cellRenderer?.(cellValue, r, i) || cellValue || '';

                    return (
                      <td
                        key={`cell_${rowId}_${c.key}`}
                        className={c.cellClassName}
                        style={{ ...getRowStyle?.(r), ...(c.cellStyle || {}) }}
                      >
                        {isValidReactNode(content) ? content : 'INVALID'}
                      </td>
                    );
                  })}
                </tr>
                {subRowRenderer != null && (
                  <tr key={`subrow_${rowId}`} className="subRow">
                    <SubRow
                      expanded={rowExpanded}
                      columnNumber={withSubRows ? columns.length + 1 : columns.length}
                    >
                      {subRowRenderer(r, i, rowExpanded)}
                    </SubRow>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
      {numberOfRows > 2 && expandable && (
        <ButtonLoad onClick={(e) => handleCollapse(e)}>
          {expanded ? 'Show Less' : 'Show More'}
        </ButtonLoad>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const ButtonLoad = styled.button`
  border: none;
  color: #36739a;
  background: transparent;
  width:320px;
  text-align: center;

  &:hover{
    cursor: pointer
  }
`;

const Table = styled.table<{
  columnWidths: string;
  expanded: boolean | undefined;
  expandable: boolean;
}>`
  background-color: white;
  margin-bottom: unset;
  background-color: white;
  border: solid 1px #dee2e6;
  width: 100%;
  border-radius: 5px;
  display: grid;
  grid-template-columns: ${({ columnWidths }) => columnWidths};
  grid-template-rows: 1fr;
  grid-auto-flow: row;
  gap: 0px 0px;
  overflow-x: auto;
  ${({ expandable, expanded }) =>
    expandable &&
    `
    transition: max-height 700ms ease;
    max-height: ${expanded ? '800px' : '183px'};
    overflow-y: ${expanded ? 'auto' : 'hidden'};
  `}

  tbody,
  thead,
  tr {
    display: contents;
  }

  tr:hover > td {
    background-color: ${RacemapColors.LightGray} !important;
  }

  tbody > .odd > td {
    background: #f6f6f6;
  }

  tr > th {
    font-weight: 600;
    border-bottom-color: ${RacemapColors.DarkGray} !important;
  }

  tr > td,
  tr > th {
    padding: 8px;
    border-bottom: solid 1px #dee2e6;
    display: flex;
    align-items: center;
    white-space: nowrap;
  }

  tr > td {
    justify-content: flex-end;
  }

  tr:last-of-type > td {
    border-bottom: unset;
  }

  .center {
    justify-content: center;
  }

  .expand-button {
    grid-column: 1;
    cursor: pointer;

    :hover {
      color: ${RacemapColors.PaleBlue};
    }
  }
`;

const SubRow = styled.td<{ expanded: boolean; columnNumber: number }>`
  padding: 0px !important;
  overflow-y: hidden;
  grid-column-start: 1;
  grid-column-end: ${({ columnNumber }) => columnNumber + 1};
  border-bottom-width: ${({ expanded }) => (expanded ? '1px' : '0px')} !important;
  border-bottom-style: solid;
  border-bottom-color: ${({ expanded }) => (expanded ? '#dee2e6' : '#ffffff00')};
  max-height: ${({ expanded }) => (expanded ? '400px' : '0px')};
  transition: max-height 500ms ease, border-bottom-color 500ms ease, border-bottom-width 500ms ease;
`;
